import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import moment from 'moment';
import 'moment/locale/fr';

import { useApi } from '../../utils/hooks';
import ErrorComponent from '../../components/Error';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendar,
    faCircleUser,
    faLocationDot,
    faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import { faFacebook } from '@fortawesome/free-brands-svg-icons';

import '../../utils/styles/Events.css';

/**
 * Formats the event date and time based on the start and end dates and times.
 * 
 * If the event lasts only one day, it returns the format:
 * "Lundi 16 octobre 2024 de 21:00 à 01:00"
 * 
 * If the event spans multiple days, it returns the format:
 * "du 25 janv. 2025 21:00 au 1 févr. 2025 00:00"
 * 
 * Additionally, if the event is today, it returns:
 * "Aujourd'hui de 21:00 à 01:00"
 *
 * @param {string} startDate - The start date of the event (format: YYYY-MM-DD).
 * @param {string} endDate - The end date of the event (format: YYYY-MM-DD).
 * @param {string} startTime - The start time of the event (format: HH:mm).
 * @param {string} endTime - The end time of the event (format: HH:mm).
 * @returns {string} - Formatted string representing the event date and time.
 */
function formatEventDate(startDate, endDate, startTime, endTime) {
    const start = moment(`${startDate}T${startTime}`);
    const end = moment(`${endDate}T${endTime}`);
    const today = moment().startOf('day');

    // If the event starts and ends on the same day or until the end of the next day
    if (start.isSame(end, 'day') || (start.isSame(end.clone().subtract(1, 'day'), 'day') && end.hour() < 6)) {
        // If the event is today
        if (start.isSame(today, 'day')) {
            return `Aujourd'hui de ${start.format('HH:mm')} à ${end.format('HH:mm')}`;
        } else {
            return `${start.format('dddd D MMM YYYY')} de ${start.format('HH:mm')} à ${end.format('HH:mm')}`;
        }
    }

    // If the event spans multiple days
    return `du ${start.format('D MMM YYYY HH:mm')} au ${end.format('D MMM YYYY HH:mm')}`;
}


function EventsDetails() {
    const { eventsId } = useParams();
    const navigate = useNavigate();

    // Call the API to fetch the data
    const { data, loading, error } = useApi(`events/${eventsId}`);

    // Display the error message if the request fails
    if (loading) return <div className="container">Chargement...</div>;

    // Display a loading message while the request is pending
    if (error) return <ErrorComponent title="Error" errorObject={error} />;

    return (
        <div>
            <div id="events-details">
                <a href={data.img}>
                    <img src={data.img} alt="événement" />
                </a>
                <button
                    onClick={() => {
                        navigate(-1);
                    }}
                    className="circle events-back"
                >
                    <FontAwesomeIcon icon={faChevronLeft} size="xl" />
                </button>
            </div>
            <div className="container">
                <p className="title">{data.name}</p>
                <div id="events-infos">
                    <p>
                        <div>
                            <FontAwesomeIcon
                                icon={faCircleUser}
                                className="fa-fw"
                                size='lg'
                            />
                        </div>
                        <span style={{ textTransform: 'capitalize' }}>{data.organizer.last_name}</span>
                    </p>
                    <p>
                        <div>

                            <FontAwesomeIcon icon={faCalendar} className="fa-fw" size='lg' />
                        </div>
                        <span>{formatEventDate(
                            data.start_date,
                            data.end_date,
                            data.start_time,
                            data.end_time
                        )}</span>
                    </p>
                    <p>
                        <div>
                            <FontAwesomeIcon
                                icon={faLocationDot}
                                className="fa-fw"
                                size='lg'
                            />
                        </div>
                        <span>{data.location}</span>
                    </p>
                    {data.facebook_link && (
                        <p className="link-fb">
                            <div>
                                <FontAwesomeIcon
                                    icon={faFacebook}
                                    className="fa-fw"
                                    size='lg'
                                />
                            </div>
                            <a target='_blank' rel="noopener noreferrer" href={data.facebook_link}>
                                Voir l'événement Facebook
                            </a>
                        </p>
                    )}
                </div>

                <p id="title-about">À quoi s'attendre</p>

                {/* Display the news content and sanitize it */}
                {/* The content is sanitized to prevent XSS attacks!!!! */}
                <div
                    id="events-details-description"
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(data.description),
                    }}
                ></div>
            </div>
        </div>
    );
}

export default EventsDetails;
